import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CoverageCheckDto,
  DeliveryZoneDto,
  DeliveryZoneInput,
  GeocodeSearchDto,
  LeadDeliveryLocationDto,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

const BASE = "/crm/delivery-zones";

type ZonesResponse = { zones: DeliveryZoneDto[]; geocodingConfigured: boolean };

/**
 * The shop's delivery zones.
 *
 * Keyed on the scoped client, never on `activeClientId` — a super-admin using
 * the switcher must not see one shop's zones under another's name.
 *
 * `enabled` is the caller's: the routes are admin- and plan-gated, and a member
 * opening Agente must not fire a request that can only answer 403.
 */
export function useDeliveryZones({ enabled }: { enabled: boolean }) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["delivery-zones", clientId],
    queryFn: () => api.get<ZonesResponse>(BASE),
    enabled: enabled && !!clientId,
  });
}

/**
 * Every zone mutation shares this shape. Invalidating `catalog-context` beside
 * the list is the browser's half of `aiService.invalidateCatalogContext`: the
 * zones ride the agent's catalog context, so its preview is stale the moment one
 * is saved.
 */
function useZoneAction<TVars>(
  mutationFn: (vars: TVars) => Promise<DeliveryZoneDto>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["delivery-zones"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-context"] });
      toast.success(successMessage);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCreateDeliveryZone() {
  return useZoneAction(
    (input: DeliveryZoneInput) => api.post<DeliveryZoneDto>(BASE, input),
    "Zona creada",
  );
}

export function useUpdateDeliveryZone() {
  return useZoneAction(
    ({ id, input }: { id: string; input: DeliveryZoneInput }) =>
      api.put<DeliveryZoneDto>(`${BASE}/${id}`, input),
    "Zona actualizada",
  );
}

/** There is no delete: orders keep pointing at the zone they were quoted in. */
export function useSetDeliveryZoneActive() {
  return useZoneAction(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<DeliveryZoneDto>(`${BASE}/${id}/active`, { isActive }),
    "Zona actualizada",
  );
}

/** "Probar una dirección" — what the agent would decide, with the zones as saved. */
export function useCheckCoverage() {
  return useMutation({
    mutationFn: (query: string) => api.post<CoverageCheckDto>(`${BASE}/check`, { query }),
    onError: (err: Error) => toast.error(err.message),
  });
}

/** The map's search box. Proxied by the backend so the Google key never reaches the browser. */
export function useGeocodeSearch() {
  return useMutation({
    mutationFn: (query: string) =>
      api
        .get<{ result: GeocodeSearchDto | null }>(
          `${BASE}/geocode?query=${encodeURIComponent(query)}`,
        )
        .then((r) => r.result),
    onError: (err: Error) => toast.error(err.message),
  });
}

/** The last delivery address the agent verified for a lead. Null for most leads. */
export function useLeadDeliveryLocation(leadId: string | undefined) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["lead-delivery-location", clientId, leadId],
    queryFn: () =>
      api
        .get<{ location: LeadDeliveryLocationDto | null }>(
          `/crm/leads/${leadId ?? ""}/delivery-location`,
        )
        .then((r) => r.location),
    enabled: !!clientId && !!leadId,
  });
}
